"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { setClientCookie } from "@/lib/cookies/client";
import type { Locale } from "@/lib/i18n/config";
import {
	isValidMarket,
	MARKET_COOKIE,
	MARKET_HINT_COOKIE,
	type Market,
} from "@/lib/market/market";

/**
 * Client-side market resolution for the ISR/edge-cached public surface.
 *
 * The page HTML is market-agnostic and rendered canonically as `indonesia`
 * (what crawlers + ID users see — see `src/app/CLAUDE.md`). Market framing is
 * swapped client-side by `<MarketGate>` / `<AdSlotClient>` / `<MarketSwitcher>`,
 * which read this context.
 *
 * Previously the resolved market arrived as a `Set-Cookie` (`market_resolved`)
 * written by middleware on every response — which made every response
 * uncacheable by Cloudflare's CDN. This provider moves the resolution to the
 * client so the HTML is cookie-free (CDN-cacheable) while framing still adapts:
 *
 *   1. `/id` → always `indonesia` (resolveMarket short-circuits on locale) —
 *      no network, no flash. This is ~93% of traffic.
 *   2. explicit override cookie (`market`) → use it directly.
 *   3. an existing `market_resolved` hint (set on a prior page) → use it.
 *   4. `/en` with none of the above → fetch `/api/market` once for geo.
 *
 * The resolved value is also written back to the `market_resolved` cookie so the
 * NEXT edge-cached page paints the right framing immediately (no fetch), and so
 * `<MarketSwitcher>`'s manual choice persists across navigations.
 *
 * SSR + first client paint both use `indonesia` (the default), so there is no
 * hydration mismatch **in the shell**; consumers reconcile after this effect
 * runs.
 *
 * That guarantee does NOT extend to a component inside a streamed `<Suspense>`
 * boundary. Such a boundary hydrates AFTER the shell's effects have committed,
 * so its first client render can already observe the RESOLVED market while the
 * server HTML was rendered with the default — a host-instance mismatch (React
 * #418), which discards the whole boundary. That is the same shape as the feed
 * bug fixed via `useHydrated()`; see `src/lib/hydration/use-hydrated.ts`. Any
 * consumer that renders differently per market inside a streamed boundary must
 * gate on `useHydrated()`.
 */

const MarketHintContext = createContext<Market>("indonesia");

export function useMarketHint(): Market {
	return useContext(MarketHintContext);
}

function readCookie(name: string): string | null {
	if (typeof document === "undefined") return null;
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
	return match ? match[1] : null;
}

function writeHint(market: Market): void {
	setClientCookie(MARKET_HINT_COOKIE, market);
}

export function MarketHintProvider({
	locale,
	children,
}: {
	locale: Locale;
	children: ReactNode;
}) {
	const [market, setMarket] = useState<Market>("indonesia");

	useEffect(() => {
		// 1. The `id` surface is always Indonesia — no geo, no fetch.
		if (locale === "id") {
			writeHint("indonesia");
			setMarket("indonesia");
			return;
		}

		// 2. An explicit manual override always wins.
		const override = readCookie(MARKET_COOKIE);
		if (override && isValidMarket(override)) {
			writeHint(override);
			setMarket(override);
			return;
		}

		// 3. A hint set on a prior page is authoritative enough to avoid a flash.
		const existing = readCookie(MARKET_HINT_COOKIE);
		if (existing && isValidMarket(existing)) {
			setMarket(existing);
			return;
		}

		// 4. `/en`, first visit, no override → resolve geo once via the API.
		let cancelled = false;
		fetch(`/api/market?locale=${locale}`, {
			headers: { accept: "application/json" },
		})
			.then((r) => (r.ok ? (r.json() as Promise<{ market?: string }>) : null))
			.then((data) => {
				if (cancelled || !data?.market || !isValidMarket(data.market)) return;
				writeHint(data.market);
				setMarket(data.market);
			})
			.catch(() => {
				// Network/edge failure — keep the `indonesia` default.
			});
		return () => {
			cancelled = true;
		};
	}, [locale]);

	return (
		<MarketHintContext.Provider value={market}>
			{children}
		</MarketHintContext.Provider>
	);
}
